import cn from 'clsx'
import Lottie from 'components/utils/Lottie'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

type Position = 'cover' | 'top' | 'center' | 'bottom'

const Container = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  pointer-events: none;

  &.position-top {
    top: 0;
    height: 50%;
  }

  &.position-bottom {
    bottom: 0;
    height: 50%;
  }

  &.position-center {
    top: 50%;
    transform: translateY(-50%);
    height: 60%;
  }

  &.position-cover {
    inset: 0;
  }
`

const LottieWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;

  &.position-top {
    align-items: flex-start;
  }

  &.position-bottom {
    align-items: flex-end;
  }

  &.position-center {
    align-items: center;
  }

  &.position-cover {
    align-items: stretch;
  }
`

type AnimationName = 'confetti' | 'sparkles' | 'world_map'

interface Props {
  /** Name of the animation to load (lazy-loaded from Animations folder) */
  animationName?: AnimationName
  /** Direct animation data (legacy support - prefer animationName for lazy loading) */
  animationData?: object
  loop?: boolean
  /** Position of the animation: cover (full), top, center, bottom */
  position?: Position
  className?: string
}

// Cache for loaded animations to avoid re-fetching
const animationCache: Map<AnimationName, object> = new Map()

// Animation file paths for lazy loading
const animationImports: Record<AnimationName, () => Promise<{ default: object }>> = {
  confetti: () => import('../Animations/flex_confetti.json'),
  sparkles: () => import('../Animations/sparkles.json'),
  world_map: () => import('../Animations/world_map.json'),
}

function WrappedLottieAnimation({
  animationName,
  animationData: directAnimationData,
  loop = false,
  position = 'cover',
  className,
}: Props) {
  const [loadedAnimationData, setLoadedAnimationData] = useState<object | null>(
    // Use cache if available, or direct data if provided
    animationName ? (animationCache.get(animationName) ?? null) : null,
  )

  // Lazy load animation data when component mounts
  useEffect(() => {
    if (!animationName || loadedAnimationData || directAnimationData) return

    const loadAnimation = async() => {
      try {
        // Check cache first
        if (animationCache.has(animationName)) {
          setLoadedAnimationData(animationCache.get(animationName)!)
          return
        }

        const module = await animationImports[animationName]()
        animationCache.set(animationName, module.default)
        setLoadedAnimationData(module.default)
      } catch(error) {
        console.error(`Failed to load animation: ${animationName}`, error)
      }
    }

    loadAnimation()
  }, [animationName, loadedAnimationData, directAnimationData])

  const animationData = directAnimationData ?? loadedAnimationData

  // Don't render anything until animation data is loaded
  if (!animationData) {
    return null
  }

  const positionClass = `position-${position}`

  return (
    <Container className={cn(positionClass, className)}>
      <LottieWrapper className={positionClass}>
        <Lottie
          animationData={animationData}
          loop={loop}
          style={{
            width: position === 'cover' ? '100%' : '120%',
            height: position === 'cover' ? '100%' : 'auto',
            maxHeight: '100%',
          }}
        />
      </LottieWrapper>
    </Container>
  )
}

export default React.memo(WrappedLottieAnimation)
